import React, { ReactNode, createContext, useContext } from "react";

import { ReturnTab, cn } from "@oneleet/utils";

interface SideTabListProps<T extends ReturnTab<U>[], U = T[number]["value"]> {
  tabs: T;
  activeTab: U;
  setActiveTab: (tab: U) => void;
  className?: string;
  children?: (props: { tab: ReturnTab<U>; key: string | number }) => ReactNode;
}

interface SideTabListContextType<
  T extends ReturnTab<U>[],
  U = T[number]["value"],
> {
  activeTab: U;
  setActiveTab: (tab: U) => void;
}

const createSideTabListContext = <
  T extends ReturnTab<U>[],
  U = T[number]["value"],
>() => createContext<SideTabListContextType<T, U> | null>(null);

const SideTabListContext = createSideTabListContext();

const useSideTabList = <T extends ReturnTab<U>[], U = T[number]["value"]>() => {
  const context = useContext(
    SideTabListContext as React.Context<SideTabListContextType<T, U> | null>,
  );
  if (!context) {
    throw new Error("useSideTabList must be used within a SideTabListProvider");
  }
  return context;
};

const SideTabListRoot = <T extends ReturnTab<U>[], U = T[number]["value"]>({
  tabs,
  activeTab,
  setActiveTab,
  className,
  children,
}: SideTabListProps<T, U>) => {
  return (
    <SideTabListContext.Provider
      // @ts-expect-error - TS doesn't like the generics here but they are accurate
      value={{ activeTab, setActiveTab }}
    >
      <div className={cn("min-w-content flex flex-col gap-2.5", className)}>
        {tabs.map((tab, index) => (
          <div key={`${tab.value}`}>
            {(() => {
              if (children) {
                return children({
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  tab: tabs.find((t) => t.value === tab.value)!,
                  key: index,
                });
              } else {
                return (
                  <SideTabListItem tab={tab} className="w-full" key={index} />
                );
              }
            })()}
          </div>
        ))}
      </div>
    </SideTabListContext.Provider>
  );
};

interface SideTabListItemProps<T extends ReturnTab<U>, U = T["value"]> {
  tab: T;
  className?: string;
}

const SideTabListItem = <T extends ReturnTab<U>, U = T["value"]>({
  tab,
  className,
}: SideTabListItemProps<T>) => {
  const { activeTab, setActiveTab } = useSideTabList();
  const isActive = activeTab === tab.value;

  return (
    <div
      className={cn(
        "text-ng-base font-ng-medium group relative flex cursor-pointer items-center gap-2 rounded-md px-3 py-2.5 transition-colors duration-300 hover:duration-0",
        isActive
          ? "bg-accent-100 text-accent-900 ring-accent-400 ring-[0.5px] ring-inset"
          : "bg-transparent hover:bg-neutral-100",
        className,
      )}
      onClick={() => setActiveTab(tab.value)}
    >
      {tab.icon &&
        React.cloneElement(tab.icon, {
          className: cn(
            "h-4 w-4",
            isActive && "text-accent-900",
            !isActive && "text-neutral-550",
          ),
        })}
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
        {tab.label}
      </span>
      <div
        className={cn(
          "bg-neutral-550 absolute left-0 top-1/2 h-1/2 w-0.5 -translate-y-1/2 scale-y-90 rounded-br-sm rounded-tr-sm opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 group-hover:duration-0",

          tab.current && "bg-accent-800 scale-x-100 opacity-100",
        )}
      />
    </div>
  );
};

const SideTabList = Object.assign(SideTabListRoot, {
  Item: SideTabListItem,
});

export { SideTabList };
